import { DateTime } from "luxon";

import { isHmoPlan, isPpoPlan } from "@/src/lib/coverage-utils";
import { PriorAuthorizationStatus } from "@/src/models/documents/prior-authorization/prior-authorization";
import { ReferralAuthorizationTeStatus } from "@/src/models/referral-authorization";

type ReferralAuthNeed = "required" | "not_required" | "unknown";

function isEndDateCurrent(endDate: string | null | undefined, now: DateTime): boolean {
  const parsed = DateTime.fromISO(endDate?.trim() ?? "", { zone: now.zone });

  return parsed.isValid && parsed.endOf("day") >= now;
}

export function classifyReferralAuthNeed(
  teStatus: ReferralAuthorizationTeStatus | null,
  planType: string | null
): ReferralAuthNeed {
  if (isHmoPlan(planType) || teStatus === ReferralAuthorizationTeStatus.REQUIRED) {
    return "required";
  }

  if (teStatus === ReferralAuthorizationTeStatus.NOT_REQUIRED || isPpoPlan(planType)) {
    return "not_required";
  }

  return "unknown";
}

export function deriveReferralAuthStatus(params: {
  currentStatus: PriorAuthorizationStatus | null;
  hasCompleteAuthFields: boolean;
  endDate: string | null;
  teStatus: ReferralAuthorizationTeStatus | null;
  planType: string | null;
  timezone?: string | null;
  now?: DateTime;
}): PriorAuthorizationStatus {
  const { currentStatus, teStatus, planType } = params;

  if (currentStatus === PriorAuthorizationStatus.APPROVED || currentStatus === PriorAuthorizationStatus.DENIED) {
    return currentStatus;
  }

  const zone = params.timezone || "utc";
  const now = params.now ?? DateTime.now().setZone(zone);

  if (params.hasCompleteAuthFields && isEndDateCurrent(params.endDate, now)) {
    return PriorAuthorizationStatus.APPROVED;
  }

  if (currentStatus && currentStatus !== PriorAuthorizationStatus.OTHER) {
    return currentStatus;
  }

  const need = classifyReferralAuthNeed(teStatus, planType);

  switch (need) {
    case "not_required":
      return PriorAuthorizationStatus.NO_AUTH_REQUIRED;
    case "required":
      return PriorAuthorizationStatus.PENDING;
    case "unknown":
      return PriorAuthorizationStatus.OTHER;
    default: {
      const _exhaustive: never = need;

      return _exhaustive;
    }
  }
}
